import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  GraduationCap, Flame, Wallet, ShieldCheck, ArrowRight, ArrowLeft,
  Trophy, Sparkles, Zap, BookOpen, Award, Lock, Loader2,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { API } from "../lib/api";
import RBLPaymentButton from "../components/pro/RBLPaymentButton";
import RecentRblActivations from "../components/pro/RecentRblActivations";

const T = {
  es: {
    tag: "// LEARN-TO-EARN",
    headline: "Estudia · Gana · Quema",
    subtitle: "Aprende crypto y recibe $R₿L reales. El único sistema donde tu educación hace crecer el ecosistema.",
    ctaMain: "🎓 Hazte PRO y empieza a ganar",
    howItWorks: "¿Cómo funciona?",
    step1: "Activa el plan PRO ($39.90 o $19.95 con R₿L)",
    step2: "Conecta tu wallet en Base L2",
    step3: "Estudia los capítulos del curso",
    step4: "Completa el quiz de cada capítulo (4/5 mínimo)",
    step5: "Recibe R₿L directamente en tu wallet",
    howMuch: "Cuánto puedes ganar",
    basicLevel: "Nivel básico",
    intermediateLevel: "Nivel intermedio",
    advancedLevel: "Nivel avanzado",
    shortChapter: "Capítulo corto",
    mediumChapter: "Capítulo medio",
    longChapter: "Capítulo largo",
    moduleBonus: "Bonus módulo completo",
    courseBonus: "Bonus curso completo + diploma",
    maxPerDay: "Máximo por día: 20.000 R₿L · 3 capítulos",
    burnTitle: "El 5% se quema — tú también ayudas",
    burnDesc: "De cada recompensa que recibes, el 5% se quema para siempre. Cada vez que aprendes, el supply baja. Menos tokens = más valor para todos los holders.",
    youReceive: "Tu wallet (R₿L reales)",
    burnedForever: "Burn address (quemado para siempre)",
    payProTitle: "Usa tus R₿L para pagar el PRO",
    payProDesc: "¿Ya tienes R₿L? Úsalos para activar el PRO con un 50% de descuento.",
    normalPrice: "Precio normal",
    normalPriceVal: "$39.90 acceso de por vida",
    rblPrice: "Pagando con R₿L",
    rblPriceVal: "$19.95 · 50% descuento",
    perfectCycle: "El ciclo perfecto",
    cycleText: "Estudias → Ganas R₿L → Pagas PRO con R₿L → Estudias más → Ganas más",
    buyRBL: "Comprar R₿L",
    activatePRO: "Activar PRO con R₿L",
    antiCheat: "Protección anti-trampa",
    antiCheatDesc: "El sistema verifica que el aprendizaje sea real:",
    antiCheat1: "Tiempo mínimo de lectura por capítulo",
    antiCheat2: "Detección de actividad real cada 30 segundos",
    antiCheat3: "Quiz de comprensión obligatorio (4/5 mínimo)",
    antiCheat4: "Cooldown entre capítulos",
    antiCheat5: "Límite diario de recompensas",
    antiCheat6: "Progresión ordenada obligatoria",
    ctaBottom: "🎓 Hazte PRO y empieza a ganar R₿L",
    ctaSubBottom: "Acceso de por vida · $39.90 o $19.95 con R₿L",
    netReward: "(reward neto · 5% ya quemado)",
  },
  en: {
    tag: "// LEARN-TO-EARN",
    headline: "Study · Earn · Burn",
    subtitle: "Learn crypto and receive real $R₿L. The only system where your education grows the ecosystem.",
    ctaMain: "🎓 Go PRO and start earning",
    howItWorks: "How it works",
    step1: "Activate PRO plan ($39.90 or $19.95 with R₿L)",
    step2: "Connect your wallet on Base L2",
    step3: "Study the course chapters",
    step4: "Complete each chapter quiz (4/5 minimum)",
    step5: "Receive R₿L directly to your wallet",
    howMuch: "How much can you earn",
    basicLevel: "Basic level",
    intermediateLevel: "Intermediate level",
    advancedLevel: "Advanced level",
    shortChapter: "Short chapter",
    mediumChapter: "Medium chapter",
    longChapter: "Long chapter",
    moduleBonus: "Complete module bonus",
    courseBonus: "Complete course bonus + diploma",
    maxPerDay: "Max per day: 20,000 R₿L · 3 chapters",
    burnTitle: "5% is burned — you help too",
    burnDesc: "From every reward you receive, 5% is burned forever. Every time you learn, the supply drops. Fewer tokens = more value for all holders.",
    youReceive: "Your wallet (real R₿L)",
    burnedForever: "Burn address (burned forever)",
    payProTitle: "Use your R₿L to pay for PRO",
    payProDesc: "Already have R₿L? Use them to activate PRO at 50% discount.",
    normalPrice: "Normal price",
    normalPriceVal: "$39.90 lifetime access",
    rblPrice: "Paying with R₿L",
    rblPriceVal: "$19.95 · 50% off",
    perfectCycle: "The perfect cycle",
    cycleText: "Study → Earn R₿L → Pay PRO with R₿L → Study more → Earn more",
    buyRBL: "Buy R₿L",
    activatePRO: "Activate PRO with R₿L",
    antiCheat: "Anti-cheat protection",
    antiCheatDesc: "The system verifies real learning:",
    antiCheat1: "Minimum reading time per chapter",
    antiCheat2: "Real activity detection every 30 seconds",
    antiCheat3: "Mandatory comprehension quiz (4/5 minimum)",
    antiCheat4: "Cooldown between chapters",
    antiCheat5: "Daily reward limit",
    antiCheat6: "Mandatory ordered progression",
    ctaBottom: "🎓 Go PRO and start earning R₿L",
    ctaSubBottom: "Lifetime access · $39.90 or $19.95 with R₿L",
    netReward: "(net reward · 5% already burned)",
  },
  sk: {
    tag: "// LEARN-TO-EARN",
    headline: "Študuj · Zarábaj · Spaľuj",
    subtitle: "Nauč sa krypto a dostávaj skutočné $R₿L. Jediný systém, kde tvoje vzdelávanie pomáha rásť ekosystému.",
    ctaMain: "🎓 Získaj PRO a začni zarábať",
    howItWorks: "Ako to funguje",
    step1: "Aktivuj PRO plán ($39.90 alebo $19.95 s R₿L)",
    step2: "Pripoj peňaženku na Base L2",
    step3: "Študuj kapitoly kurzu",
    step4: "Dokonči kvíz každej kapitoly (minimum 4/5)",
    step5: "Dostávaj R₿L priamo do peňaženky",
    howMuch: "Koľko môžeš zarobiť",
    basicLevel: "Základná úroveň",
    intermediateLevel: "Stredná úroveň",
    advancedLevel: "Pokročilá úroveň",
    shortChapter: "Krátka kapitola",
    mediumChapter: "Stredná kapitola",
    longChapter: "Dlhá kapitola",
    moduleBonus: "Bonus za celý modul",
    courseBonus: "Bonus za celý kurz + diplom",
    maxPerDay: "Max za deň: 20 000 R₿L · 3 kapitoly",
    burnTitle: "5 % sa spáli — ty tiež pomáhaš",
    burnDesc: "Z každej odmeny sa 5 % spáli navždy. Zakaždým, keď sa učíš, zásoba klesá. Menej tokenov = väčšia hodnota pre všetkých.",
    youReceive: "Tvoja peňaženka (skutočné R₿L)",
    burnedForever: "Burn adresa (spálené navždy)",
    payProTitle: "Použi svoje R₿L na zaplatenie PRO",
    payProDesc: "Máš už R₿L? Použi ich na aktiváciu PRO so 50 % zľavou.",
    normalPrice: "Bežná cena",
    normalPriceVal: "$39.90 doživotný prístup",
    rblPrice: "Platba s R₿L",
    rblPriceVal: "$19.95 · 50 % zľava",
    perfectCycle: "Dokonalý cyklus",
    cycleText: "Študuj → Zarábaj R₿L → Plať PRO s R₿L → Študuj viac → Zarábaj viac",
    buyRBL: "Kúpiť R₿L",
    activatePRO: "Aktivovať PRO s R₿L",
    antiCheat: "Anti-podvodná ochrana",
    antiCheatDesc: "Systém overuje skutočné učenie:",
    antiCheat1: "Minimálny čas čítania na kapitolu",
    antiCheat2: "Detekcia aktivity každých 30 sekúnd",
    antiCheat3: "Povinný kvíz porozumenia (min 4/5)",
    antiCheat4: "Cooldown medzi kapitolami",
    antiCheat5: "Denný limit odmien",
    antiCheat6: "Povinná zoradená progresia",
    ctaBottom: "🎓 Získaj PRO a začni zarábať R₿L",
    ctaSubBottom: "Doživotný prístup · $39.90 alebo $19.95 s R₿L",
    netReward: "(netto · 5 % už spálené)",
  }, de: { tag: "// LEARN-TO-EARN", headline: "Lernen · Verdienen · Verbrennen", subtitle: "Lerne Krypto und erhalte echte $R₿L. Das einzige System, bei dem deine Bildung das Ökosystem wachsen lässt.", ctaMain: "🎓 Werde PRO und fange an zu verdienen", howItWorks: "Wie funktioniert es?", step1: "Aktiviere den PRO-Plan ($39.90 oder $19.95 mit R₿L)", step2: "Verbinde deine Wallet auf Base L2", step3: "Studiere die Kurskapitel", step4: "Bestehe das Quiz jedes Kapitels (mindestens 4/5)", step5: "Erhalte R₿L direkt in deine Wallet", howMuch: "Wie viel du verdienen kannst", basicLevel: "Grundstufe", intermediateLevel: "Mittelstufe", advancedLevel: "Fortgeschrittene Stufe", shortChapter: "Kurzes Kapitel", mediumChapter: "Mittleres Kapitel", longChapter: "Langes Kapitel", moduleBonus: "Bonus vollständiges Modul", courseBonus: "Bonus vollständiger Kurs + Diplom", maxPerDay: "Maximum pro Tag: 20.000 R₿L · 3 Kapitel", burnTitle: "5% werden verbrannt — du hilfst auch", burnDesc: "Von jeder Belohnung, die du erhältst, werden 5% für immer verbrannt. Jedes Mal, wenn du lernst, sinkt das Supply. Weniger Token = mehr Wert für alle Holder.", youReceive: "Deine Wallet (echte R₿L)", burnedForever: "Burn-Adresse (für immer verbrannt)", payProTitle: "Nutze deine R₿L, um PRO zu bezahlen", payProDesc: "Hast du bereits R₿L? Nutze sie, um PRO mit 50% Rabatt zu aktivieren.", normalPrice: "Normalpreis", normalPriceVal: "$39.90 lebenslanger Zugang", rblPrice: "Zahlung mit R₿L", rblPriceVal: "$19.95 · 50% Rabatt", perfectCycle: "Der perfekte Kreislauf", cycleText: "Du lernst → Du verdienst R₿L → Du bezahlst PRO mit R₿L → Du lernst mehr → Du verdienst mehr", buyRBL: "R₿L kaufen", activatePRO: "PRO mit R₿L aktivieren", antiCheat: "Anti-Betrugs-Schutz", antiCheatDesc: "Das System überprüft, dass das Lernen echt ist:", antiCheat1: "Mindestlesezeit pro Kapitel", antiCheat2: "Erkennung echter Aktivität alle 30 Sekunden", antiCheat3: "Obligatorisches Verständnis-Quiz (mindestens 4/5)", antiCheat4: "Cooldown zwischen Kapiteln", antiCheat5: "Tägliches Belohnungslimit", antiCheat6: "Obligatorische geordnete Progression", ctaBottom: "🎓 Werde PRO und fange an R₿L zu verdienen", ctaSubBottom: "Lebenslanger Zugang · $39.90 oder $19.95 mit R₿L", netReward: "(Nettobelohnung · 5% bereits verbrannt)" }, "zh-CN": { tag: "// LEARN-TO-EARN", headline: "学习 · 赚取 · 销毁", subtitle: "学习加密货币知识并获得真实的 $R₿L。唯一一个让你的学习推动生态系统增长的系统。", ctaMain: "🎓 成为 PRO 会员并开始赚取", howItWorks: "如何运作？", step1: "激活 PRO 计划（$39.90 或使用 R₿L 支付 $19.95）", step2: "在 Base L2 上连接你的钱包", step3: "学习课程章节", step4: "完成每章测验（最低 4/5）", step5: "直接在你的钱包中接收 R₿L", howMuch: "你能赚多少", basicLevel: "基础级别", intermediateLevel: "中级级别", advancedLevel: "高级级别", shortChapter: "短章节", mediumChapter: "中等章节", longChapter: "长章节", moduleBonus: "完整模块奖励", courseBonus: "完整课程奖励 + 证书", maxPerDay: "每日最高：20,000 R₿L · 3 个章节", burnTitle: "5% 被销毁 — 你也在贡献", burnDesc: "你收到的每笔奖励中，5% 将被永久销毁。每次你学习，供应量就会减少。更少的代币 = 所有持有者的价值更高。", youReceive: "你的钱包（真实 R₿L）", burnedForever: "销毁地址（永久销毁）", payProTitle: "使用你的 R₿L 支付 PRO", payProDesc: "已经拥有 R₿L？使用它们以 50% 折扣激活 PRO。", normalPrice: "正常价格", normalPriceVal: "$39.90 终身访问", rblPrice: "使用 R₿L 支付", rblPriceVal: "$19.95 · 50% 折扣", perfectCycle: "完美循环", cycleText: "学习 → 赚取 R₿L → 用 R₿L 支付 PRO → 学习更多 → 赚取更多", buyRBL: "购买 R₿L", activatePRO: "使用 R₿L 激活 PRO", antiCheat: "防作弊保护", antiCheatDesc: "系统验证学习是否真实：", antiCheat1: "每章最低阅读时间", antiCheat2: "每 30 秒检测真实活动", antiCheat3: "强制理解测验（最低 4/5）", antiCheat4: "章节之间的冷却时间", antiCheat5: "每日奖励限制", antiCheat6: "强制按顺序进行", ctaBottom: "🎓 成为 PRO 会员并开始赚取 R₿L", ctaSubBottom: "终身访问 · $39.90 或使用 R₿L 支付 $19.95", netReward: "（净奖励 · 已销毁 5%）" },
};

// Net rewards (after 5% burn) — gross × 0.95
const REWARDS = [
  { level: "basicLevel", color: "#F5D76E", values: [{ k: "shortChapter", v: 475 }, { k: "mediumChapter", v: 950 }, { k: "longChapter", v: 1900 }] },
  { level: "intermediateLevel", color: "#A855F7", values: [{ k: "shortChapter", v: 1425 }, { k: "mediumChapter", v: 2850 }, { k: "longChapter", v: 4750 }] },
  { level: "advancedLevel", color: "#fb7185", values: [{ k: "shortChapter", v: 2850 }, { k: "mediumChapter", v: 5700 }, { k: "longChapter", v: 9500 }] },
];

const fmt = (n) => Number(n).toLocaleString("es-ES");

const Section = ({ children, className = "", testid }) => (
  <motion.section
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    className={`mx-auto max-w-4xl px-6 md:px-10 ${className}`}
    data-testid={testid}
  >
    {children}
  </motion.section>
);

const Card = ({ children, className = "", borderColor = "rgba(109,40,217,0.3)", testid }) => (
  <div
    className={`rounded-2xl border bg-[#111118] p-6 md:p-8 backdrop-blur ${className}`}
    style={{ borderColor }}
    data-testid={testid}
  >
    {children}
  </div>
);

export default function LearnToEarnPage() {
  const { lang } = useLanguage();
  const { token, user } = useAuth();
  const t = T[lang] || T.en || T.es;
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const isPro = !!user?.is_pro;

  const startStripeCheckout = async () => {
    if (checkoutLoading) return;
    setCheckoutError(null);
    setCheckoutLoading(true);
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          origin_url: window.location.origin,
          plan: "pro_lifetime",
        }),
      });
      const data = await res.clone().json().catch(() => ({}));
      if (!res.ok || !data?.url) throw new Error(data?.detail || "Checkout failed");
      window.location.href = data.url;
    } catch (e) {
      setCheckoutError(e.message || "Error");
      setCheckoutLoading(false);
    }
  };

  const scrollToRblBlock = () => {
    const el = document.getElementById("pay-pro-rbl");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="min-h-screen w-full bg-[#0a0a0f] pt-24 pb-24 text-white">
      {/* Back button — exits to home/previous page */}
      <Section className="!mb-4 !mt-0" testid="l2e-back-wrap">
        <button
          type="button"
          onClick={() => {
            if (window.history.length > 1) window.history.back();
            else window.location.href = "/";
          }}
          className="group inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs font-bold text-white/70 backdrop-blur transition hover:-translate-x-0.5 hover:border-orange-400/40 hover:bg-white/[0.06] hover:text-white"
          data-testid="l2e-back-btn"
        >
          <ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-1" />
          {lang === "en" ? "Back" : lang === "sk" ? "Späť" : "Volver"}
        </button>
      </Section>

      {/* HERO */}
      <Section className="mt-6" testid="l2e-hero">
        <p className="font-mono text-[12px] font-semibold uppercase tracking-[0.28em] text-orange-400" data-testid="l2e-tag">
          {t.tag}
        </p>
        <h1 className="font-display mt-4 text-4xl font-black leading-tight sm:text-5xl md:text-6xl" data-testid="l2e-headline">
          {t.headline}
        </h1>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/70 md:text-lg" data-testid="l2e-subtitle">
          {t.subtitle}
        </p>
        <div className="mt-8">
          <button
            type="button"
            onClick={isPro ? () => { window.location.href = "/academy"; } : startStripeCheckout}
            disabled={checkoutLoading}
            className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-7 py-3.5 text-sm font-black uppercase tracking-[0.16em] text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(255,140,0,0.55)] disabled:opacity-60 md:text-base"
            data-testid="l2e-cta-hero"
          >
            {checkoutLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {isPro ? (lang === "en" ? "🎓 Start earning now" : lang === "sk" ? "🎓 Začni zarábať" : "🎓 Empieza a ganar ya") : t.ctaMain}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
          {checkoutError && (
            <p className="mt-2 text-[11px] text-red-300/90" data-testid="l2e-cta-hero-error">{checkoutError}</p>
          )}
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section className="mt-20" testid="l2e-how-it-works">
        <h2 className="text-2xl font-bold md:text-3xl">{t.howItWorks}</h2>
        <Card className="mt-6" testid="l2e-steps">
          <ol className="space-y-4">
            {[t.step1, t.step2, t.step3, t.step4, t.step5].map((s, i) => (
              <li key={i} className="flex items-start gap-4" data-testid={`l2e-step-${i + 1}`}>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-500/15 font-mono text-sm font-bold text-orange-300 ring-1 ring-orange-400/30">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="pt-1.5 text-sm leading-relaxed text-white/85 md:text-base">{s}</p>
              </li>
            ))}
          </ol>
        </Card>
      </Section>

      {/* HOW MUCH */}
      <Section className="mt-20" testid="l2e-how-much">
        <h2 className="text-2xl font-bold md:text-3xl">{t.howMuch}</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {REWARDS.map((g) => (
            <Card
              key={g.level}
              borderColor={`${g.color}55`}
              className="!p-5"
              testid={`l2e-rewards-${g.level}`}
            >
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: g.color }}>
                {t[g.level]}
              </p>
              <ul className="mt-4 space-y-2.5">
                {g.values.map((r) => (
                  <li key={r.k} className="flex items-center justify-between text-sm">
                    <span className="text-white/65">{t[r.k]}</span>
                    <span className="font-mono font-bold text-white">{fmt(r.v)} R₿L</span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
        <p className="mt-3 font-mono text-[10px] text-white/40">{t.netReward}</p>

        {/* Bonuses */}
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <Card borderColor="rgba(245,215,110,0.35)" className="!p-5" testid="l2e-bonus-module">
            <div className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-[#F5D76E]" />
              <p className="text-sm font-bold text-white">✨ {t.moduleBonus}</p>
            </div>
            <p className="mt-2 font-mono text-2xl font-black text-[#F5D76E]">+23.750 R₿L</p>
          </Card>
          <Card borderColor="rgba(168,85,247,0.35)" className="!p-5" testid="l2e-bonus-course">
            <div className="flex items-center gap-2">
              <Award className="h-5 w-5 text-violet-300" />
              <p className="text-sm font-bold text-white">🏆 {t.courseBonus}</p>
            </div>
            <p className="mt-2 font-mono text-2xl font-black text-violet-300">+95.000 R₿L</p>
          </Card>
        </div>

        <p className="mt-5 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-center font-mono text-xs text-white/60" data-testid="l2e-max-per-day">
          {t.maxPerDay}
        </p>
      </Section>

      {/* BURN — left border orange */}
      <Section className="mt-20" testid="l2e-burn">
        <Card
          borderColor="rgba(249,115,22,0.3)"
          className="border-l-[3px] !border-l-orange-500"
        >
          <div className="flex flex-col items-start gap-4 md:flex-row md:items-center">
            <div className="rounded-full bg-orange-500/15 p-3 ring-1 ring-orange-400/30">
              <Flame className="h-6 w-6 text-orange-300" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white md:text-2xl">🔥 {t.burnTitle}</h2>
              <p className="mt-2 text-sm leading-relaxed text-white/70">{t.burnDesc}</p>
            </div>
          </div>
          <div className="mt-6 grid gap-3 md:grid-cols-2">
            <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/[0.06] p-4" data-testid="l2e-dist-student">
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-emerald-300">95%</p>
              <p className="mt-1 inline-flex items-center gap-1.5 text-sm font-bold text-white">
                <Wallet className="h-4 w-4 text-emerald-300" /> {t.youReceive}
              </p>
            </div>
            <div className="rounded-xl border border-orange-400/30 bg-orange-500/[0.06] p-4" data-testid="l2e-dist-burn">
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-orange-300">5%</p>
              <p className="mt-1 inline-flex items-center gap-1.5 text-sm font-bold text-white">
                <Flame className="h-4 w-4 text-orange-400" /> {t.burnedForever}
              </p>
            </div>
          </div>
        </Card>
      </Section>

      {/* PAY PRO WITH R₿L — left border violet */}
      <Section className="mt-12" testid="l2e-pay-pro">
        <Card
          borderColor="rgba(109,40,217,0.35)"
          className="border-l-[3px] !border-l-violet-700"
        >
          <h2 className="text-xl font-bold text-white md:text-2xl">{t.payProTitle}</h2>
          <p className="mt-2 text-sm leading-relaxed text-white/70">{t.payProDesc}</p>

          <div className="mt-6 grid gap-3 md:grid-cols-2">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/55">{t.normalPrice}</p>
              <p className="mt-1 text-base font-bold text-white">{t.normalPriceVal}</p>
            </div>
            <div className="rounded-xl border border-violet-400/30 bg-violet-500/[0.08] p-4">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-violet-300">{t.rblPrice}</p>
              <p className="mt-1 text-base font-bold text-violet-200">{t.rblPriceVal}</p>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-orange-400/25 bg-gradient-to-r from-orange-500/10 via-violet-500/10 to-orange-500/10 p-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-orange-300">{t.perfectCycle}</p>
            <p className="mt-2 flex items-center gap-2 text-sm font-bold text-white">
              <Zap className="h-4 w-4 text-orange-300" />
              {t.cycleText}
            </p>
          </div>

          <div className="mt-6 flex flex-wrap gap-3" id="pay-pro-rbl">
            <Link
              to="/shadow/tokens"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-xs font-bold text-white backdrop-blur transition hover:-translate-y-0.5 hover:border-orange-400/50 hover:bg-white/10"
              data-testid="l2e-buy-rbl"
            >
              {t.buyRBL} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <button
              type="button"
              onClick={scrollToRblBlock}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-violet-600 to-purple-600 px-5 py-2.5 text-xs font-black uppercase tracking-[0.16em] text-white transition hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(168,85,247,0.5)]"
              data-testid="l2e-activate-pro"
            >
              {t.activatePRO} <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Embedded R₿L payment block */}
          {!isPro && (
            <div className="mt-6 rounded-2xl border border-violet-400/25 bg-black/30 p-4 md:p-5" data-testid="l2e-pay-rbl-embed">
              <RBLPaymentButton />
            </div>
          )}

          {/* Social proof — last on-chain PRO activations */}
          <div className="mt-6" data-testid="l2e-recent-activations">
            <RecentRblActivations limit={5} />
          </div>
          {isPro && (
            <div
              className="mt-6 rounded-xl border border-emerald-400/30 bg-emerald-500/[0.06] p-4 text-sm text-emerald-200"
              data-testid="l2e-pay-rbl-already-pro"
            >
              ✓ {lang === "en" ? "You already have PRO — start earning R₿L now." : lang === "sk" ? "Už máš PRO — začni zarábať R₿L." : "Ya tienes PRO — empieza a ganar R₿L ahora."}
            </div>
          )}
        </Card>
      </Section>

      {/* ANTI-CHEAT */}
      <Section className="mt-12" testid="l2e-anti-cheat">
        <h2 className="text-2xl font-bold md:text-3xl">
          <ShieldCheck className="mr-2 inline h-7 w-7 text-cyan-300" />
          {t.antiCheat}
        </h2>
        <p className="mt-2 text-sm text-white/65">{t.antiCheatDesc}</p>
        <Card borderColor="rgba(0,229,255,0.25)" className="mt-6 !p-6" testid="l2e-anti-cheat-card">
          <ul className="grid gap-3 md:grid-cols-2">
            {[t.antiCheat1, t.antiCheat2, t.antiCheat3, t.antiCheat4, t.antiCheat5, t.antiCheat6].map((line, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-white/85" data-testid={`l2e-anti-${i + 1}`}>
                <Lock className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />
                {line}
              </li>
            ))}
          </ul>
        </Card>
      </Section>

      {/* FINAL CTA */}
      <Section className="mt-16" testid="l2e-final-cta">
        <Card
          borderColor="rgba(249,115,22,0.4)"
          className="text-center !p-8 md:!p-12"
          testid="l2e-final-cta-card"
        >
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-amber-600 shadow-[0_0_30px_rgba(255,140,0,0.45)]">
            <GraduationCap className="h-7 w-7 text-black" />
          </div>
          <h2 className="mt-5 text-2xl font-black text-white md:text-3xl">{t.ctaBottom}</h2>
          <p className="mt-2 text-xs text-white/60">{t.ctaSubBottom}</p>
          <button
            type="button"
            onClick={isPro ? () => { window.location.href = "/academy"; } : startStripeCheckout}
            disabled={checkoutLoading}
            className="group mt-7 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-500 to-amber-600 px-7 py-3.5 text-sm font-black uppercase tracking-[0.16em] text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(255,140,0,0.6)] disabled:opacity-60 md:text-base"
            data-testid="l2e-cta-final"
          >
            {checkoutLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookOpen className="h-4 w-4" />}
            {isPro ? (lang === "en" ? "🎓 Start earning now" : lang === "sk" ? "🎓 Začni zarábať" : "🎓 Empieza a ganar ya") : t.ctaBottom}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
        </Card>
      </Section>
    </main>
  );
}
